import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  Alert,
  Box,
  Button,
  Chip,
  Container,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Snackbar,
  Stack,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
  Autocomplete,
  createFilterOptions,
} from "@mui/material";
import RefreshIcon from "@mui/icons-material/Refresh";
import MaterialReactTable from "material-react-table";
import { MRT_Localization_ES } from "material-react-table/locales/es";
import Navbar from "../Navbar";
import { corregirCorreoFallido, listarCorreosFallidos } from "./correosFallidos.service";

const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

const MOTIVOS = {
  formato_invalido: "Formato inválido",
  sin_correo: "Sin correo",
  excluido: "Excluido",
  usuario_inexistente: "Usuario inexistente",
  dominio_inexistente: "Dominio inexistente",
  rechazo_usuario: "Usuario inexistente",
  rechazo_dominio: "Dominio inexistente",
};

const RESULTADOS = {
  omitido: "Omitido",
  rechazado: "Rechazado",
};

function textoCodigo(codigo, mapa) {
  if (!codigo) return "—";
  const clave = String(codigo).trim().toLowerCase();
  if (mapa[clave]) return mapa[clave];
  return clave.replace(/_/g, " ");
}

function formatearFecha(value) {
  if (!value) return "—";
  const texto = String(value);
  const conHora = texto.match(/^(\d{4})-(\d{2})-(\d{2})[ T](\d{2}):(\d{2})/);
  if (conHora) return `${conHora[3]}/${conHora[2]}/${conHora[1]} ${conHora[4]}:${conHora[5]}`;
  const soloDia = texto.match(/^(\d{4})-(\d{2})-(\d{2})/);
  if (soloDia) return `${soloDia[3]}/${soloDia[2]}/${soloDia[1]}`;
  return texto;
}

function nombreCliente(row) {
  return row?.razon_social || row?.razon_social_factura || "—";
}

function nombreSucursal(row) {
  return row?.sucursal || "—";
}

function etiquetaTipo(valor) {
  if (valor === "granel") return "Granel";
  if (valor === "envasado") return "Envasado";
  return valor || "—";
}

function esDireccion(row) {
  return String(row?.fuente || "").toLowerCase() === "direccion";
}

function etiquetaDonde(row) {
  if (esDireccion(row)) return "Dirección de entrega";
  if (String(row?.fuente || "").toLowerCase() === "cliente") return "Datos";
  return row?.fuente || "—";
}

function textoDonde(row) {
  if (esDireccion(row) && row?.nombre_direccion) {
    return `Dirección de entrega — ${row.nombre_direccion}`;
  }
  return etiquetaDonde(row);
}

function filaCoincideBusqueda(row, filtro) {
  const q = String(filtro || "").trim().toLowerCase();
  if (!q) return true;
  const texto = [
    nombreCliente(row),
    row?.id_factura,
    row?.cuit,
    row?.sucursal,
    row?.destinatario,
    row?.mails_actuales,
    textoDonde(row),
  ]
    .filter((valor) => valor != null && String(valor).trim() !== "")
    .join(" ")
    .toLowerCase();
  return texto.includes(q);
}

function ayudaGuardado(row) {
  if (esDireccion(row)) {
    return "Se guarda en la dirección de entrega y la factura vuelve a la cola.";
  }
  if (mailActual(row) === "") {
    return "Datos está vacío: se carga este mail y la factura vuelve a la cola.";
  }
  return "Se guarda en Datos y la factura vuelve a la cola.";
}

function mailActual(row) {
  const valor = row?.mails_actuales;
  if (valor == null || String(valor).trim() === "") return "";
  return String(valor);
}

function mailsActualesDeError(body) {
  if (!body || typeof body !== "object") return undefined;
  if (body.mails_actuales !== undefined) return body.mails_actuales;
  if (body.data?.mails_actuales !== undefined) return body.data.mails_actuales;
  return undefined;
}

const filter = createFilterOptions({
  stringify: (option) =>
    option?.nombre || option?.title || (typeof option === "string" ? option : ""),
});

function sucursalesDelUsuario() {
  return (localStorage.getItem("suc_auth") || "")
    .split(",")
    .map((id) => id.trim())
    .filter(Boolean);
}

const Dato = ({ label, value }) => (
  <Box>
    <Typography variant="caption" color="text.secondary" display="block">
      {label}
    </Typography>
    <Typography variant="body2">{value || "—"}</Typography>
  </Box>
);

export default function CorreosFallidos() {
  const [filas, setFilas] = useState([]);
  const [loading, setLoading] = useState(true);
  const [errorLista, setErrorLista] = useState("");
  const [tipo, setTipo] = useState("todos");
  const [filtros, setFiltros] = useState({ sucursales: [] });
  const [editando, setEditando] = useState(null);
  const [email, setEmail] = useState("");
  const [errorDialogo, setErrorDialogo] = useState("");
  const [guardando, setGuardando] = useState(false);
  const [aviso, setAviso] = useState({ open: false, message: "", severity: "success" });

  const cargar = useCallback(async () => {
    setLoading(true);
    setErrorLista("");
    try {
      const data = await listarCorreosFallidos();
      setFilas(data);
    } catch (error) {
      setFilas([]);
      setErrorLista(error.message || "No se pudo cargar el listado.");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    cargar();
  }, [cargar]);

  const sucursalesAutorizadas = useMemo(() => sucursalesDelUsuario(), []);

  const listaAutorizada = useMemo(() => {
    if (!sucursalesAutorizadas.length) return [];
    return filas.filter((fila) =>
      sucursalesAutorizadas.includes(String(fila.id_sucursal))
    );
  }, [filas, sucursalesAutorizadas]);

  const sucursalesUnicas = useMemo(() => {
    const mapa = new Map();
    listaAutorizada.forEach((fila) => {
      if (fila.id_sucursal == null) return;
      const id = String(fila.id_sucursal);
      if (!mapa.has(id)) {
        mapa.set(id, {
          id,
          nombre: fila.sucursal || id,
        });
      }
    });
    return [...mapa.values()].sort((a, b) => a.nombre.localeCompare(b.nombre, "es"));
  }, [listaAutorizada]);

  const handleChange = (campo, opcionesUnicas) => (_, newValue) => {
    if (newValue.find((option) => option.all)) {
      setFiltros((f) => ({
        ...f,
        [campo]: f[campo].length === opcionesUnicas.length ? [] : opcionesUnicas,
      }));
      return;
    }
    setFiltros((f) => ({ ...f, [campo]: newValue }));
  };

  const visibles = useMemo(() => {
    return listaAutorizada.filter((fila) => {
      if (tipo !== "todos" && String(fila.tipo || "").toLowerCase() !== tipo) return false;
      if (
        filtros.sucursales.length > 0 &&
        !filtros.sucursales.some((item) => String(item.id) === String(fila.id_sucursal))
      ) {
        return false;
      }
      return true;
    });
  }, [listaAutorizada, tipo, filtros]);

  const abrir = (fila) => {
    setEditando(fila);
    setEmail("");
    setErrorDialogo("");
  };

  const cerrar = () => {
    if (guardando) return;
    setEditando(null);
    setEmail("");
    setErrorDialogo("");
  };

  const aplicarMailsActuales = (idLog, mails) => {
    setFilas((prev) =>
      prev.map((fila) => (fila.id_log === idLog ? { ...fila, mails_actuales: mails } : fila))
    );
    setEditando((prev) => (prev && prev.id_log === idLog ? { ...prev, mails_actuales: mails } : prev));
  };

  const guardar = async () => {
    if (!editando || guardando) return;
    const nuevo = email.trim();
    const fallido = String(editando.destinatario || "").trim();

    if (!emailRegex.test(nuevo)) {
      setErrorDialogo("El mail es inválido.");
      return;
    }
    if (fallido && nuevo.toLowerCase() === fallido.toLowerCase()) {
      setErrorDialogo("El mail es igual al que falló.");
      return;
    }

    setGuardando(true);
    setErrorDialogo("");
    try {
      const body = await corregirCorreoFallido(editando.id_log, nuevo);
      setEditando(null);
      setEmail("");
      setAviso({
        open: true,
        severity: "success",
        message: body.msg || "Mail guardado. La factura volvió a la cola de envío.",
      });
      await cargar();
    } catch (error) {
      const status = error.status;
      if (status === 404) {
        setEditando(null);
        setAviso({
          open: true,
          severity: "warning",
          message: error.message || "Ese intento ya no está para corregir.",
        });
        await cargar();
        return;
      }
      if (status === 409) {
        const actuales = mailsActualesDeError(error.body);
        if (actuales !== undefined) aplicarMailsActuales(editando.id_log, actuales);
        const textoActual =
          actuales === undefined ? "" : String(actuales).trim() || "(vacío)";
        setErrorDialogo(
          `${error.message || "Ese correo ya no está en la ficha."}${
            textoActual ? ` Texto actual: ${textoActual}.` : ""
          }`
        );
        return;
      }
      setErrorDialogo(error.message || "No se pudo guardar el mail.");
    } finally {
      setGuardando(false);
    }
  };

  const columns = useMemo(
    () => [
      {
        accessorKey: "fecha",
        header: "Fecha",
        size: 128,
        minSize: 120,
        maxSize: 140,
        Cell: ({ cell }) => formatearFecha(cell.getValue()),
      },
      {
        accessorKey: "tipo",
        header: "Tipo",
        size: 88,
        minSize: 80,
        maxSize: 96,
        Cell: ({ cell }) => {
          const valor = String(cell.getValue() || "").toLowerCase();
          const granel = valor === "granel";
          return (
            <Chip
              size="small"
              label={etiquetaTipo(valor)}
              sx={{
                backgroundColor: granel ? "#E8F6F3" : "#EAF2F8",
                color: granel ? "#0E6655" : "#1A5276",
                fontWeight: 600,
              }}
            />
          );
        },
      },
      {
        id: "cliente",
        accessorFn: (row) => nombreCliente(row),
        header: "Cliente",
        size: 220,
        minSize: 160,
        Cell: ({ row }) => (
          <Typography variant="body2" sx={{ fontWeight: 600, color: "#1F618D" }}>
            {nombreCliente(row.original)}
          </Typography>
        ),
      },
      {
        accessorKey: "id_factura",
        header: "Factura",
        size: 72,
        minSize: 64,
        maxSize: 84,
      },
      {
        accessorKey: "cuit",
        header: "CUIT",
        size: 112,
        minSize: 100,
        maxSize: 124,
        Cell: ({ cell }) => cell.getValue() || "—",
      },
      {
        id: "sucursal",
        accessorFn: (row) => nombreSucursal(row),
        header: "Sucursal",
        size: 110,
        minSize: 96,
        maxSize: 130,
      },
      {
        id: "donde",
        accessorFn: (row) => textoDonde(row),
        header: "Origen",
        size: 150,
        minSize: 130,
        maxSize: 170,
        Cell: ({ row }) => (
          <Box>
            <Typography variant="body2">{etiquetaDonde(row.original)}</Typography>
            {esDireccion(row.original) && row.original.nombre_direccion ? (
              <Typography variant="caption" color="text.secondary" display="block">
                {row.original.nombre_direccion}
              </Typography>
            ) : null}
          </Box>
        ),
      },
      {
        accessorKey: "destinatario",
        header: "Mail que falló",
        size: 220,
        minSize: 160,
        Cell: ({ cell }) => cell.getValue() || "—",
      },
      {
        accessorKey: "motivo",
        header: "Motivo",
        size: 130,
        minSize: 110,
        maxSize: 160,
        Cell: ({ row }) => (
          <Box>
            <Typography variant="body2">{textoCodigo(row.original.motivo, MOTIVOS)}</Typography>
            {row.original.resultado ? (
              <Typography variant="caption" color="text.secondary">
                {textoCodigo(row.original.resultado, RESULTADOS)}
              </Typography>
            ) : null}
          </Box>
        ),
      },
    ],
    []
  );

  return (
    <Box sx={{ backgroundColor: "#fff", minHeight: "100vh" }}>
      <Navbar />
      <Container maxWidth="xl" sx={{ pt: "20px", pb: 4, px: 2 }}>
        <Stack
          direction="row"
          alignItems="center"
          justifyContent="space-between"
          spacing={2}
          sx={{ mb: 2 }}
        >
          <Box>
            <Typography variant="h5" fontWeight={700}>
              Correos no enviados
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Corregí el mail. La factura volverá a la cola de envío.
            </Typography>
          </Box>
          <ToggleButtonGroup
            exclusive
            size="small"
            value={tipo}
            onChange={(_, valor) => {
              if (valor) setTipo(valor);
            }}
          >
            <ToggleButton value="todos">Todos</ToggleButton>
            <ToggleButton value="granel">Granel</ToggleButton>
            <ToggleButton value="envasado">Envasado</ToggleButton>
          </ToggleButtonGroup>
        </Stack>

        <Stack
          direction="row"
          alignItems="center"
          spacing={2}
          flexWrap="wrap"
          useFlexGap
          sx={{ mb: 2 }}
        >
          <Autocomplete
            multiple
            size="small"
            disableCloseOnSelect
            options={sucursalesUnicas}
            value={filtros.sucursales}
            isOptionEqualToValue={(option, value) => option.id === value.id}
            filterOptions={(options, params) => {
              const filtered = filter(options, params);
              return [{ title: "Seleccionar todas", all: true }, ...filtered];
            }}
            onChange={handleChange("sucursales", sucursalesUnicas)}
            getOptionLabel={(option) => option.title || option.nombre || ""}
            renderOption={(props, option) => (
              <li {...props} key={option.all ? "todas" : option.id}>
                {option.all ? (
                  <strong>
                    {filtros.sucursales.length === sucursalesUnicas.length
                      ? "Quitar selección"
                      : "Seleccionar todas"}
                  </strong>
                ) : (
                  option.nombre
                )}
              </li>
            )}
            renderTags={(value, getTagProps) =>
              value.map((option, index) => (
                <Box key={option.id || option.title} component="span" sx={{ mr: 0.5 }}>
                  <Chip label={option.title || option.nombre} {...getTagProps({ index })} />
                </Box>
              ))
            }
            renderInput={(params) => (
              <TextField {...params} label="Sucursal" placeholder="Seleccionar..." />
            )}
            sx={{ minWidth: 220 }}
          />
          <Button
            variant="outlined"
            color="secondary"
            onClick={() => setFiltros({ sucursales: [] })}
          >
            Limpiar filtros
          </Button>
        </Stack>

        {errorLista ? (
          <Alert severity="error" sx={{ mb: 2 }}>
            {errorLista}
          </Alert>
        ) : null}

        <MaterialReactTable
          columns={columns}
          data={visibles}
          getRowId={(row, index) =>
            row?.id_log != null ? String(row.id_log) : `fila-${index}`
          }
          state={{ showProgressBars: loading }}
          localization={MRT_Localization_ES}
          filterFns={{
            correosFallidos: (row, _columnId, filterValue) =>
              filaCoincideBusqueda(row?.original, filterValue),
          }}
          globalFilterFn="correosFallidos"
          enableColumnActions={false}
          enableColumnFilters={false}
          enableDensityToggle={false}
          enableFullScreenToggle={false}
          enableHiding={false}
          enableRowActions
          positionActionsColumn="last"
          displayColumnDefOptions={{
            "mrt-row-actions": {
              header: "Acciones",
              size: 100,
              minSize: 90,
              maxSize: 110,
            },
          }}
          initialState={{
            density: "compact",
            showGlobalFilter: true,
            columnOrder: [
              "fecha",
              "tipo",
              "cliente",
              "id_factura",
              "cuit",
              "sucursal",
              "donde",
              "destinatario",
              "motivo",
              "mrt-row-actions",
            ],
          }}
          muiTableHeadCellProps={{
            sx: {
              fontSize: 13,
              color: "white",
              fontWeight: "normal",
              backgroundColor: "#2471A3",
              borderColor: "black",
              whiteSpace: "nowrap",
              px: 1,
            },
          }}
          muiTableBodyCellProps={{
            sx: {
              borderColor: "black",
              px: 1,
              backgroundColor: "#fff",
              whiteSpace: "nowrap",
            },
          }}
          muiTablePaperProps={{
            elevation: 0,
            sx: {
              width: "100%",
              border: "1px solid",
              borderColor: "divider",
              borderRadius: 2,
              backgroundColor: "#fff",
            },
          }}
          muiTableContainerProps={{
            sx: { width: "100%", maxWidth: "100%", backgroundColor: "#fff" },
          }}
          muiTableProps={{
            sx: { tableLayout: "auto", width: "100%" },
          }}
          muiSearchTextFieldProps={{
            placeholder: "Buscar cliente, factura o correo",
            size: "small",
          }}
          renderTopToolbarCustomActions={() => (
            <Stack direction="row" alignItems="center" spacing={1}>
              <Typography variant="subtitle1" fontWeight={600}>
                {visibles.length > 0
                  ? `${visibles.length} pendiente${visibles.length === 1 ? "" : "s"}`
                  : "Sin resultados"}
              </Typography>
              <Button
                size="small"
                startIcon={<RefreshIcon />}
                onClick={cargar}
                disabled={loading}
              >
                Actualizar
              </Button>
            </Stack>
          )}
          renderEmptyRowsFallback={() => (
            <Typography
              variant="body2"
              color="text.secondary"
              sx={{ p: 3, textAlign: "center" }}
            >
              No hay correos para corregir.
            </Typography>
          )}
          renderRowActions={({ row }) => (
            <Button size="small" variant="outlined" onClick={() => abrir(row.original)}>
              Corregir
            </Button>
          )}
        />
      </Container>

      <Dialog open={Boolean(editando)} onClose={cerrar} fullWidth maxWidth="sm">
        <DialogTitle>Corregir correo</DialogTitle>
        <DialogContent sx={{ display: "flex", flexDirection: "column", gap: 2, pt: "8px !important" }}>
          {editando ? (
            <>
              <Dato label="Cliente" value={nombreCliente(editando)} />
              <Dato label="Factura" value={editando.id_factura} />
              <Dato label="CUIT" value={editando.cuit} />
              <Dato label="Sucursal" value={nombreSucursal(editando)} />
              <Dato label="Origen" value={textoDonde(editando)} />
              <Dato label="Mail que falló" value={editando.destinatario} />
              <TextField
                autoFocus
                label="Mail nuevo"
                type="email"
                value={email}
                onChange={(event) => {
                  setEmail(event.target.value);
                  setErrorDialogo("");
                }}
                onKeyDown={(event) => {
                  if (event.key === "Enter") {
                    event.preventDefault();
                    guardar();
                  }
                }}
                placeholder="nuevo@cliente.com"
                helperText={ayudaGuardado(editando)}
                fullWidth
                size="small"
                disabled={guardando}
                error={Boolean(errorDialogo)}
              />
              {errorDialogo ? <Alert severity="warning">{errorDialogo}</Alert> : null}
            </>
          ) : null}
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={cerrar} disabled={guardando}>
            Cancelar
          </Button>
          <Button variant="contained" onClick={guardar} disabled={guardando || !email.trim()}>
            {guardando ? "Guardando…" : "Guardar"}
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={aviso.open}
        autoHideDuration={5000}
        onClose={() => setAviso((prev) => ({ ...prev, open: false }))}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      >
        <Alert
          severity={aviso.severity}
          onClose={() => setAviso((prev) => ({ ...prev, open: false }))}
          variant="filled"
        >
          {aviso.message}
        </Alert>
      </Snackbar>
    </Box>
  );
}
